import { EventEmitter } from 'eventemitter3';
import { loggerManager } from '..';
import { IS_ANDROID } from '../utils/environment';
import eventLog from '../utils/log/event-log';


enum PressureState {
  NOMINAL = 'nominal',
  FAIR = 'fair',
  SERIOUS = 'serious',
  CRITICAL = 'critical'
}

enum PressureEvent {
  STATE_CHANGED = 'state-changed',
}
interface PressureDetectorEventTypes {
  [PressureEvent.STATE_CHANGED]: [{ type: 'cpu', state: PressureState }]
}
interface LoAFRecord {
  time: string;
  duration: number;
  scripts: {
    duration: number;
    invoker: string;
    invokerType: string;
    sourceURL: string;
    sourceFunctionName: string;
  }[];
}

const LOAF_FLUSH_INTERVAL = 10_000;
const LOAF_FIELD_MAX_LENGTH = 120;

function truncate(str: string, max = LOAF_FIELD_MAX_LENGTH) {
  if (str.length <= max) return str;
  const headLen = Math.floor(max * 0.7);
  const tailLen = max - headLen;
  return `${str.slice(0, headLen)}…${str.slice(-tailLen)}`;
}
// 阻塞检测阈值，只有 duration 超过此值的长帧才会被累计阻塞耗时并记录到 buffer
const LOAF_BLOCKING_THRESHOLD = 200;


// iframe 需要
// ref: https://developer.chrome.com/docs/web-platform/compute-pressure#records
class PressureDetector extends EventEmitter<PressureDetectorEventTypes> {
  observer: any;
  state: PressureState = PressureState.NOMINAL;

  private _loafObserver: PerformanceObserver | null = null;
  private _loafBuffer: LoAFRecord[] = [];
  private _loafFlushTimer: ReturnType<typeof setInterval> | null = null;
  // 累计阻塞耗时(ms)，用于 2s 上报周期内统计主线程超过 100ms 阻塞任务总耗时
  private _totalBlockingDuration = 0;

  get stateNum() {
    switch (this.state) {
      case PressureState.NOMINAL: return 1;
      case PressureState.FAIR: return 2;
      case PressureState.SERIOUS: return 3;
      case PressureState.CRITICAL: return 4;
    }
  }

  /**
   * 获取累计的主线程阻塞耗时(ms)，用于上报周期内做差值统计
   */
  get totalBlockingDuration() {
    return this._totalBlockingDuration;
  }

  constructor() {
    super();
    this.onPressureChange = this.onPressureChange.bind(this);
  }


  start() {
    this.startCPUObserver();
    this.startLoAFDetection();
  }

  // https://w3c.github.io/compute-pressure/#policy-control
  // iframe 需要加权限配置才能使用。
  // <iframe src="https://third-party.com" allow="compute-pressure"/></iframe>
  private async startCPUObserver() {
    if (this.observer) return;
    // The Compute Pressure API is supported.
    try {
      // 安卓有该 API，但是不支持，先不使用 https://caniuse.com/mdn-api_pressureobserver
      if ('PressureObserver' in window && !IS_ANDROID) {
        // @ts-ignore
        this.observer = new PressureObserver(this.onPressureChange);
        await this.observer.observe('cpu', { sampleInterval: 2000 });
        // 目前 Chrome 125 只支持 cpu 统计，后续会支持 gpu，温度统计。
        // const supportedSources = PressureObserver.supportedSources;
      }
    } catch (error) {
      eventLog.uploadEvent({ log: 'stat-pressure-detector-start-failed', error: (error as Error) });
    }
  }

  // Long Animation Frames API: 检测主线程阻塞超过 100ms 的帧
  // ref: https://developer.mozilla.org/en-US/docs/Web/API/Performance_API/Long_animation_frame_timing
  private startLoAFDetection() {
    try {
      if (!('PerformanceObserver' in window) ||
        !PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame') || this._loafObserver) {
        return;
      }

      this._loafObserver = new PerformanceObserver(list => {
        this.processLoAFEntries(list.getEntries() as any[]);
      });

      this._loafObserver.observe({ type: 'long-animation-frame', buffered: true });

      this._loafFlushTimer = setInterval(() => this.flushLoAFBuffer(), LOAF_FLUSH_INTERVAL);
    } catch (error) {
      // LoAF detection is best-effort, ignore errors
    }
  }

  private processLoAFEntries(entries: any[]) {
    try {
      for (const entry of entries) {
        if (entry.duration < LOAF_BLOCKING_THRESHOLD) continue;

        const record: LoAFRecord = {
          time: new Date(performance.timeOrigin + entry.startTime).toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 } as any),
          duration: Math.round(entry.duration),
          scripts: (entry.scripts || []).map((s: any) => ({
            duration: Math.round(s.duration),
            invoker: truncate(s.invoker || ''),
            invokerType: s.invokerType || '',
            sourceURL: s.sourceURL || '',
            sourceFunctionName: truncate(s.sourceFunctionName || '')
          }))
        };
        this._totalBlockingDuration += record.duration;
        this._loafBuffer.push(record);
      }
    } catch (e) { /* best-effort */ }
  }

  private flushLoAFBuffer() {
    // 主动拉取尚未通过回调派发的 entry（主线程阻塞恢复后可在此处捕获）
    if (this._loafObserver) {
      this.processLoAFEntries(this._loafObserver.takeRecords() as any[]);
    }

    if (this._loafBuffer.length === 0) return;

    try {
      const records = this._loafBuffer.splice(0);
      const urlIndexMap = new Map<string, number>();
      const getUrlIndex = (url: string) => {
        if (!url) return '';
        if (!urlIndexMap.has(url)) urlIndexMap.set(url, urlIndexMap.size + 1);
        return `[${urlIndexMap.get(url)}]`;
      };

      // 按 scripts 签名分组合并
      const groups = new Map<string, { count: number; timeRange: string[]; duration: number[] }>();
      for (const r of records) {
        const scriptKey = r.scripts.length > 0
          ? r.scripts.map(s => `${s.invokerType}:${s.invoker} ${s.sourceFunctionName} ${getUrlIndex(s.sourceURL)}`).join(' | ')
          : 'no script info';
        const group = groups.get(scriptKey);
        if (group) {
          group.count += 1;
          group.timeRange.push(r.time);
          group.duration.push(r.duration);
        } else {
          groups.set(scriptKey, { count: 1, timeRange: [r.time], duration: [r.duration] });
        }
      }

      const header = '  count | duration(avg) | time range                  | scripts';
      const lines = [...groups.entries()].map(([scriptKey, g]) => {
        const avgDur = Math.round(g.duration.reduce((a, b) => a + b, 0) / g.count);
        const timeRange = g.count === 1 ? g.timeRange[0] : `${g.timeRange[0]}~${g.timeRange[g.timeRange.length - 1]}`;
        return `  ${String(g.count).padStart(5)} | ${String(avgDur).padStart(13)}ms | ${timeRange.padEnd(27)} | ${scriptKey}`;
      });

      const urlLegend = urlIndexMap.size > 0
        ? `\n  sources: ${[...urlIndexMap.entries()].map(([url, i]) => `[${i}]${url}`).join(' ')}`
        : '';
      loggerManager.warn(`[LoAF] ${records.length} long frames (>${LOAF_BLOCKING_THRESHOLD}ms) in last ${LOAF_FLUSH_INTERVAL / 1000}s:${urlLegend}\n${header}\n${lines.join('\n')}`);
    } catch (e) { /* best-effort */ }
  }

  onPressureChange(records: any[]) {
    const prevStateNum = this.stateNum;
    const current = records[records.length - 1];
    this.state = current.state;

    if (this.stateNum > 3 || prevStateNum > 3) {
      loggerManager.info(`${current.source}: ${current.state}`);
    }
    this.emit(PressureEvent.STATE_CHANGED, { type: current.source, state: this.state });
  }

  destroy() {
    try {
      this.observer?.disconnect();
      this.observer = null;
    } catch (error) {
      eventLog.uploadEvent({ log: 'stat-pressure-detector-destroy-failed', error: (error as Error) });
    }

    if (this._loafObserver) {
      this._loafObserver.disconnect();
      this._loafObserver = null;
    }
    if (this._loafFlushTimer) {
      clearInterval(this._loafFlushTimer);
      this._loafFlushTimer = null;
    }
    this.flushLoAFBuffer();
  }
}

const pressureDetector = new PressureDetector();


// 测试主线程阻塞代码
// TODO: 100% 阻塞的情况无法检测出来，后续可能还是得借助 worker 来实现
// function pressureMainThread(duration = 5000, ratio = 0.5) {
//   const clampedRatio = Math.max(0, Math.min(ratio, 1));
//   const endTime = performance.now() + duration;

//   // 100% 阻塞：直接 busy loop，不让出主线程
//   if (clampedRatio === 1) {
//     while (performance.now() < endTime) {}
//     return;
//   }

//   const interval = 150;
//   const busyTime = interval * clampedRatio;
//   const idleTime = interval - busyTime;

//   function tick() {
//     const now = performance.now();
//     if (now >= endTime) return;

//     const blockUntil = now + busyTime;
//     while (performance.now() < blockUntil) {}

//     setTimeout(tick, idleTime);
//   }

//   tick();
// }
// window.pressureMainThread = pressureMainThread;
export default pressureDetector;
